import { useTranslation } from "react-i18next";
import React, { useState, useEffect } from "react";
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth } from "./firebaseConfig";
import { getUserProfile, sendPasswordReset } from "./firestoreService";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  InputAdornment,
  Divider,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";

function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [resetSuccess, setResetSuccess] = useState("");
  const navigate = useNavigate();

  const location = useLocation();
  const [signUpSuccess, setSignUpSuccess] = useState("");

  const { t } = useTranslation();

  useEffect(() => {
    if (location.state?.successMessage) {
      setSignUpSuccess(location.state.successMessage);
      // Clear the state from history so the message doesn't reappear on back navigation
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");
    setResetSuccess("");
    setSignUpSuccess(""); // Clear success messages on new login attempt

    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );
      const user = userCredential.user;

      // Fetch Firestore profile
      const profile = await getUserProfile(user.uid);
      // Either the account hasn't been activated yet (no users doc) or
      // the user is inactive. Either way: bounce them and show a hint.
      if (!profile || profile.status !== "active") {
        await auth.signOut();
        setError(t("login.notActivated"));
        setIsLoading(false);
        return;
      }
      // Active user: do NOT navigate imperatively here. App.js's /login route
      // performs the role-aware redirect once AuthContext resolves currentUser
      // + role (free_user -> /app/beta, everyone else -> /app/dashboard).
      // Navigating now races the async auth-state update and can briefly land
      // on the "*" -> "/" fallback before the /app routes mount. The spinner
      // stays up until that redirect unmounts this component.
    } catch (err) {
      console.error("Login failed:", err);
      setError(t("auth.loginFailed"));
    } finally {
      // This will only run for failed logins now, as successful ones navigate away
      if (auth.currentUser) {
        // No need to set loading false if we are navigating
      } else {
        setIsLoading(false);
      }
    }
  };

  const handlePasswordReset = async () => {
    if (!email) {
      setError(t("login.enterEmailForReset"));
      return;
    }
    setIsLoading(true);
    setError("");
    setResetSuccess("");

    try {
      await sendPasswordReset(email);
      setResetSuccess(t("login.resetEmailSent"));
    } catch (err) {
      console.error("Password reset failed:", err);
      setError(t("login.resetEmailFailed"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Typography component="h1" variant="h4">
          KB Quick
        </Typography>
        <Typography component="h2" variant="h5" sx={{ mt: 1 }}>
          Login
        </Typography>
        <Box component="form" onSubmit={handleLogin} sx={{ mt: 1 }}>
          <TextField
            margin="normal"
            required
            fullWidth
            id="email"
            label={t("auth.emailAddress")}
            name="email"
            autoComplete="email"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={isLoading}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            name="password"
            label={t("common.password")}
            type={passwordVisible ? "text" : "password"}
            id="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={isLoading}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {" "}
                  <IconButton
                    onClick={() => setPasswordVisible(!passwordVisible)}
                    edge="end"
                  >
                    {" "}
                    {passwordVisible ? <VisibilityOff /> : <Visibility />}{" "}
                  </IconButton>{" "}
                </InputAdornment>
              ),
            }}
          />

          {/* ✅ RENDER ANY OF THE ALERTS */}
          {error && (
            <Alert severity="error" sx={{ width: "100%", mt: 2 }}>
              {error}
            </Alert>
          )}
          {resetSuccess && (
            <Alert severity="success" sx={{ width: "100%", mt: 2 }}>
              {resetSuccess}
            </Alert>
          )}
          {signUpSuccess && (
            <Alert severity="success" sx={{ width: "100%", mt: 2 }}>
              {signUpSuccess}
            </Alert>
          )}

          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 1 }}
            disabled={isLoading}
          >
            {isLoading ? <CircularProgress size={24} /> : t("auth.signIn")}
          </Button>

          <Button
            fullWidth
            onClick={handlePasswordReset}
            disabled={isLoading}
            size="small"
          >
            {t("auth.forgotPassword")}
          </Button>

          {/* ✅ NEW SIGN UP SECTION */}
          <Divider sx={{ my: 2 }}>{t("login.newToKBQuick")}</Divider>

          <Button
            onClick={() => navigate("/signup")}
            fullWidth
            variant="outlined"
            sx={{ mb: 1 }}
          >
            {t("login.requestAccount")}
          </Button>
        </Box>
      </Box>
    </Container>
  );
}

export default Login;
